import { PaginatedParams } from 'core/http/pagination';
import { DeclarationExternalEntity } from './enums/common/declaration-external-entity';
import { DeclarationInternalType } from './enums/common/declaration-internal-type';
import { DeclarationStatuses } from './enums/common/declaration-status';
import { ElectronicTransportDocument } from './ireland/electronic-transport-document';
import { IrelandEntrySummaryDeclaration } from './ireland/entry-summary-declaration';
import { IrelandExportDeclaration } from './ireland/export-declaration';
import { IrelandH7ImportDeclaration } from './ireland/h7-import-declaration';
import { IrelandImportDeclaration } from './ireland/import-declaration';
import { CdsDeclaration } from './uk/cds-declaration';
import { DeclarationMeta } from './declaration-source-meta';
import { UkGvmsRecord } from './uk/gvms-declaration';
import { ArrivalAtExitDeclaration } from './ireland/arrival-at-exit-declaration';
import { IrelandPbnRecord } from './ireland/pbn-declaration';
import { IrelandTemporaryStorageDeclaration } from './ireland/temporary-storage-declaration';
import { NctsDeclaration } from './ireland/ncts-declaration';
import { MessageTypes } from './enums/common/declaration-types';
import { SortDirection } from 'utils/tableHelpers';
import { TaricEvalStatus } from 'views/declarations/ireland/import/h1/invoice/InvoiceModal';
import { Individual } from 'store/individuals/individual';
import { DeclarationDocument } from 'views/declarations/declaration-tabs/documentation/types';
import { ICS2EnsDeclaration } from './eu/ics2/ics2-ens-declaration';

export interface NotifyEmails {
    customerEmail?: string;
    clerkEmail?: string;
}

export enum GetDeclarationsSortParameter {
    ID = 'ID',
    EXTERNAL_ENTITY = 'EXTERNAL_ENTITY',
    STATUS = 'STATUS',
    INTERNAL_TYPE = 'INTERNAL_TYPE',
    UPDATED_AT = 'UPDATED_AT',
    CREATED_AT = 'CREATED_AT',
}

export type GetArchivedDeclarationsParams = Partial<{
    sort: GetDeclarationsSortParameter;
    page: number;
    size: number;
    declarationExternalEntity: DeclarationExternalEntity;
    declarationInternalType: DeclarationInternalType;
    declarationStatus: DeclarationStatuses;
    declarationType: MessageTypes;
    individualIds: string[];
    query: string;
    queryBy: string[];
}>;

export type GetDeclarationsParams = Partial<{
    sortDirection: SortDirection;
    sort: GetDeclarationsSortParameter;
    page: number;
    size: number;
    customerId: string;
    jobId: string;
    isCoreTemplate: boolean;
    isActionRequired: boolean;
    declarationExternalEntity: DeclarationExternalEntity;
    declarationInternalType: DeclarationInternalType[];
    declarationStatus: DeclarationStatuses[];
    startDate: string;
    endDate: string;
    scheduledJobId: string;
    declarationType: MessageTypes[];
    createdBy: string;
    brokerId: string;
    hasMrn: boolean;
    individualIds: string[];
    query: string;
    queryBy: string[];
    archived: boolean;
}>;

export interface DeclarationFilterFields extends PaginatedParams {
    jobId?: string;
    costumerId?: string;
    declarationStatus?: DeclarationStatuses[];
    declarationExternalEntity?: DeclarationExternalEntity[] | DeclarationExternalEntity;
    declarationInternalType?: DeclarationInternalType[] | DeclarationInternalType;
    query?: string;
    isCoreTemplate?: boolean;
    createdAt?: string;
}

export interface DeclarationDocumentsFilterFields extends PaginatedParams {
    query?: string;
    customerId?: string;
    declarationId?: string;
}

export interface DeclarationDocumentResponse {
    id: string;
    link?: string;
    fileSize?: number;
    contentType?: string;
    documentType?: string;
    filename?: string;
    description?: string;
    documentDescription?: string;
    documentReference?: string;
    codeMappingAdditionalDocument?: string;
    createdAt?: string;
}

export interface MirrorDetails {
    groupId: string;
    sourceDeclarationId: string;
    // One-based position in the split group, and the size of the group when it was created.
    index: number;
    count: number;
    // Missing until the DUCR is generated (incomplete drafts).
    ducrBase?: string;
    ducrReference?: string;
    ducrYearDigit: number;
    // Job-wide part number. Older mirrors do not have it and use `index` instead.
    ducrPart?: number;
}

export interface MirroringData {
    createdDeclarationsCount: number;
    createdDeclarationsByType: {
        declarationExternalEntity: DeclarationExternalEntity;
        declarationInternalType: DeclarationInternalType;
        declarationName: MessageTypes;
        count: number;
    }[];
}

export interface Declaration {
    id?: string;

    isCoreTemplate?: boolean;

    customerId?: string;

    individualId?: string;
    individual?: {
        customerId: string;
        email: string;
        id: string;
        name: string;
        phone: string;
        type: string;
    };

    status: DeclarationStatuses;

    jobId?: string;

    declarationExternalEntity?: DeclarationExternalEntity;

    declarationInternalType?: DeclarationInternalType;

    routing?: RoutingStatus;

    uploadedFiles?: DeclarationDocument[];

    archived?: boolean;
    mirrored?: boolean;
    mirrorDeclarationIdLink?: string;
    mirrorDetails?: MirrorDetails;
    mirroring?: MirroringData;

    cdsDeclaration?: CdsDeclaration;
    irelandImportDeclaration?: IrelandImportDeclaration;
    irelandH7ImportDeclaration?: IrelandH7ImportDeclaration;
    ieExportDeclaration?: IrelandExportDeclaration;
    ieImportTemporaryStorageDeclaration?: IrelandTemporaryStorageDeclaration;
    entrySummaryDeclaration?: IrelandEntrySummaryDeclaration;
    ics2ENSDeclaration?: ICS2EnsDeclaration;
    ieImportElectronicTransportDocument?: ElectronicTransportDocument;
    ieArrivalAtExitDeclaration?: ArrivalAtExitDeclaration;
    gvmsDeclaration?: UkGvmsRecord;
    preBoardingNotification?: IrelandPbnRecord;
    ieNctsDeclaration?: NctsDeclaration;
    ukNctsDeclaration?: NctsDeclaration;
    createdAt?: string;
    updatedAt?: string;
    mrn?: string;
    lrn?: string;
    acceptanceDate?: string;
    referenceNumber?: string;
    templateId?: string;
    meta?: DeclarationMeta;
    actionRequired?: boolean;
    /**GVMS specific */
    inspectionRequired?: boolean;
    taxCalculationDiffers?: boolean;
    manifestDetailsMismatch?: boolean;

    submittedAt?: string;
    submittedBy?: Individual;
}

export enum DeclarationSortParameters {
    ID = 'ID',
    MESSAGE_TYPE = 'MESSAGE_TYPE',
    STATUS = 'STATUS',
    EXTERNAL_ENTITY = 'EXTERNAL_ENTITY',
    INTERNAL_TYPE = 'INTERNAL_TYPE',
    EXPORTER = 'EXPORTER',
    IMPORTER = 'IMPORTER',
    DECLARANT = 'DECLARANT',
    CREATED_AT = 'CREATED_AT',
    DESCRIPTION_OF_GOODS = 'DESCRIPTION_OF_GOODS',
    DOCUMENTS = 'DOCUMENTS',
}

export interface Notifiable<TNotification> {
    notifications?: TNotification[];
}

export interface Amendable {
    amendmentAccepted?: boolean;
    amendmentRejected?: boolean;
    revenueAmendmentRequested?: boolean;
    userAmendmentSubmitted?: boolean;
}

export interface HasDocuments {
    documentsUploadRequested?: boolean;
    documentsUploaded?: boolean;
    documentsPresentationRequested?: boolean;
    uploadedDocuments?: string[];
}

export type DeclarationAndAgent = Omit<Declaration, 'id'> & { id: string; agent?: string };

export enum RoutingStatus {
    GREEN = 'GREEN',
    ORANGE = 'ORANGE',
    YELLOW = 'YELLOW',
    RED = 'RED',
}

export interface DeclarationHistory {
    id?: string;
    declarationId: string;
    type: 'SUBMIT' | 'AMEND';
    declaration: Declaration;
    description?: string;
    createdAt: string;
    createdBy: string;
    creatorName: any;
}

export type TeeInvoiceExtracationResponse = Declaration & {
    taricEvalStatus: TaricEvalStatus;
};

export type NotePayload = {
    title: string;
    note: string;
};

export type DeclarationNote = {
    id: string;
    declarationId: string;
    title: string;
    note: string;
    creator: Individual;
    createdAt: string;
    updatedAt: string;
};

export type GeneratedDeclarationDocumentStatus = 'DRAFT' | 'FINAL';
export type GeneratedDeclarationDocumentType = 'SAD' | 'CLEARANCE_SLIP';
